import has from 'lodash/has';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import moment from 'moment';
import React from 'react';

import { Button } from 'new/elements/support/Button';
import { Alert, CircularProgress } from '@mui/material';
import { ArrowDownward as DownArrowIcon } from '@mui/icons-material';

import DataSourceIcon from '../data_sources/DataSourceIcon';
import DataSetIcon from '../sidebar/icons/DataSetIcon';
import DataSourcesIcon from '../sidebar/icons/DataSourcesIcon';
import DestinationIcon from '../sidebar/icons/SubscribeIcon';

import {
  ROUTE_DATA_SOURCES,
  ROUTE_DESTINATIONS,
  ROUTE_MY_DATA_SETS,
} from '../../constants/pathnames';

import { SUPPORT_KEY_LINEAGE } from '../../constants/supportPageKeys';

import { LINEAGE_RESOUCE_TYPES } from '../../constants/resourceTypes';

import RoutePrefixContext from '../../../new/contexts/RoutePrefixContext';
import DateUtils from '../../lib/DateUtils';
import { getConnectionOrVendorType } from '../../lib/ResourceUtils';
import { BannerDescription } from 'new/elements/BannerDescription';
import { Box } from 'new/elements/Box';
import { TextField } from 'new/elements/TextField';
import TypographySupport from 'new/elements/TypographySupport';
import { interpolateCSSVariable } from 'new/theme';
import { Link } from 'react-router-dom';
import { EmptyState } from 'new/elements/EmptyState/EmptyState';

import './RecordLineage.scss';

const description = 'Visualize the complete journey of data from its Source to Destination.';
const instructions =
  "Get the tracker id of a record from Destination and paste it below to retrace the record's journey through Nexla.";

const styles = {
  titleRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'white',
    // marginTop: 16,
    // boxShadow: 'rgba(42, 63, 86, 0.1) 0px 0px 10px 0px',
    borderBottom: '1px solid ' + interpolateCSSVariable('--color-outline'),
    paddingTop: 14,
    paddingBottom: 14,
    margin: 0,
    top: 0,
    borderRadius: 0,
  },
  textArea: {
    display: 'flex',
    flexDirection: 'column',
  },
  heading: {
    paddingLeft: 20,
    marginBottom: 0,
    marginTop: 0,
    fontSize: 20,
  },
  subheading: {
    fontSize: 12,
    paddingLeft: 20,
  },
  header: {
    padding: 12,
  },
  headerTitle: {
    fontSize: 14,
  },
  settingsContent: {
    padding: 12,
    fontSize: 12,
  },
  errorContent: {
    padding: 12,
    backgroundColor: 'rgba(217, 31, 32, 1)',
    fontSize: 12,
    color: '#fff',
  },
  resultsContent: {
    padding: 0,
    minHeight: 100,
    fontSize: 12,
  },
  ctaContainer: {
    padding: '0px 16px 16px 0px',
    display: 'flex',
    justifyContent: 'center',
  },
  buttonText: {
    fontSize: 12,
  },
  buttonStyle: {
    width: 'auto',
    lineHeight: '30px',
    height: 'auto',
    marginRight: 0,
    marginLeft: 10,
    border: 4,
  },
  introText: {
    fontSize: 12,
  },
  notConfiguredText: {
    marginTop: 8,
    fontSize: 12,
    color: 'red',
    fontWeight: 500,
  },
  configurationTitle: {
    fontSize: 12,
    fontWeight: 500,
  },
  arrowIcon: {
    width: 45,
    height: 45,
    color: '#357BFF',
    viewBox: '0 0 24 24',
  },
};

const BUTTON_LABEL_STYLE = {
  fontSize: 12,
  paddingLeft: 18,
  paddingRight: 18,
};

const PARAM_DISPLAY_NAMES = {
  Source: {
    source_id: 'Name ( ID )',
    id: 'Name ( ID )',
    source: 'File Name / API URL',
    record_number: 'Row number',
    initial_ingest_timestamp: 'Ingested At',
    table: 'Database Table',
    from: 'Range Start Value (For DB modes)',
    to: 'Range End Value (For DB modes)',
  },
  Dataset: {
    id: 'Name ( ID )',
  },
  Sink: {
    id: 'Name ( ID )',
  },
};

class RecordLineageView extends React.Component {
  static contextType = RoutePrefixContext;

  constructor(props) {
    super(props);
    this.state = {
      trackerId: null,
      /**
       * If the user has requested a tracker id, we don't want to show the intial empty state
       */
      resquestedTrackerId: false,
    };
  }

  componentDidMount() {
    this.props.setContextualSupport(SUPPORT_KEY_LINEAGE);
  }
  componentWillUnmount() {
    this.props.unsetContextualSupport(SUPPORT_KEY_LINEAGE);
  }

  render() {
    const { trackerId } = this.state;

    const fetchButton =
      !isNil(trackerId) && trackerId !== '' ? (
        <Button variant="contained" onClick={this._fetchRecordLineage}>
          <span style={BUTTON_LABEL_STYLE}>Trace Record Lineage</span>
        </Button>
      ) : (
        <Button disabled variant="contained" onClick={this._fetchRecordLineage}>
          <span style={BUTTON_LABEL_STYLE}>Trace Record Lineage</span>
        </Button>
      );

    const resultsView = this._renderResultsView();

    return (
      <div className="lineage-view">
        <BannerDescription title="Trace Record Lineage" description={description} />
        <div className="lineage-settings">
          <Box className="settings-card" style={styles.settingsContent}>
            <TypographySupport variant="body2">{instructions}</TypographySupport>
            <div className="input-row">
              <div style={{ flexGrow: 1 }}>
                <TextField
                  fullWidth
                  label="Tracker Id"
                  multiline={true}
                  value={this.state.trackerId || ''}
                  onChange={(event) => {
                    this._onTextInputChanged('trackerId', event.target.value);
                  }}
                />
              </div>
            </div>
            <div className="cta-button">{fetchButton}</div>
          </Box>
        </div>
        <div className="results-view" style={{ margin: 20 }}>
          {resultsView}
        </div>
      </div>
    );
  }

  _renderResultsView = () => {
    const {
      isLoading,
      recordLineage: { lineage, isError, error },
      sources,
      sinks,
      metaInfoById,
      vendors,
    } = this.props;
    const { resquestedTrackerId } = this.state;

    const hasResources = has(lineage, 'resources') && !isNil(lineage.resources);

    /**
     * If the user has not requested a tracker id
     * and the lineage is not loading
     * and there are no resources
     * - show the initial empty state
     */
    if (!resquestedTrackerId && !isLoading && !hasResources) {
      return (
        <div className="results-card-container">
          <EmptyState
            title="Trace a Record’s Journey"
            description="Enter a Tracker ID to retrace a record’s journey from its Source to its Destination."
            tip={
              <>
                Need help finding a Tracker ID?{' '}
                <Link to="/integrate/destinations" target="_blank">
                  Check your Destination logs
                </Link>{' '}
                (opens in new tab) or ask your admin.
              </>
            }
          />
        </div>
      );
    }

    if (isLoading === true) {
      return (
        <div className="loading-background">
          <CircularProgress color="secondary" />
        </div>
      );
    }

    if (isError === true) {
      return (
        <Alert severity="error">
          There was an error in fetching record lineage. {` Reason: ${error.message}`}
        </Alert>
      );
    }

    let resourceCards = hasResources
      ? map(lineage.resources, (res, idx) => {
          const resourceType = res.resource_type;
          let label = resourceType;
          let iconView = null;
          let resourceTypeIconView = null;
          let resourceTypeLabel = null;
          switch (resourceType) {
            case LINEAGE_RESOUCE_TYPES.data_source:
              {
                const sourceId = has(res, 'source_id')
                  ? res.source_id
                  : has(res, 'id')
                    ? res.id
                    : null;

                if (sourceId && has(sources, sourceId) && metaInfoById) {
                  const connectionOrVendorType = getConnectionOrVendorType(
                    sources[sourceId],
                    vendors,
                    'source_type',
                    'vendor_id',
                  );
                  const connectionInfo = has(metaInfoById, connectionOrVendorType)
                    ? metaInfoById[connectionOrVendorType]
                    : null;
                  resourceTypeLabel = connectionInfo ? connectionInfo.displayName : null;
                  resourceTypeIconView = connectionInfo ? (
                    <DataSourceIcon
                      icon={connectionInfo.iconSm}
                      name={connectionInfo.name}
                      displayName={false}
                    />
                  ) : null;
                }

                label = 'Source';
                iconView = <DataSourcesIcon htmlColor={'rgb(137, 148, 155)'} />;
              }

              break;

            case LINEAGE_RESOUCE_TYPES.data_set:
              label = 'Dataset';
              iconView = <DataSetIcon htmlColor={'rgb(137, 148, 155)'} />;
              break;

            case LINEAGE_RESOUCE_TYPES.data_sink:
              {
                const sinkId = has(res, 'id') ? res.id : null;
                if (sinkId && has(sinks, sinkId) && metaInfoById) {
                  const connectionOrVendorType = getConnectionOrVendorType(
                    sinks[sinkId],
                    vendors,
                    'sink_type',
                    'vendor_id',
                  );
                  const connectionInfo = has(metaInfoById, connectionOrVendorType)
                    ? metaInfoById[connectionOrVendorType]
                    : null;
                  resourceTypeLabel = connectionInfo ? connectionInfo.displayName : null;
                  resourceTypeIconView = connectionInfo ? (
                    <DataSourceIcon
                      icon={connectionInfo.iconSm}
                      name={connectionInfo.name}
                      displayName={false}
                    />
                  ) : null;
                }

                label = 'Destination';
                iconView = <DestinationIcon htmlColor={'rgb(137, 148, 155)'} />;
              }

              break;

            default:
          }

          const connectorView =
            idx !== 0 ? (
              <div className="results-connector">
                <DownArrowIcon
                  fontSize="large"
                  style={styles.arrowIcon}
                  viewBox={styles.arrowIcon.viewBox}
                />
              </div>
            ) : null;

          return (
            <div key={idx}>
              {connectorView}
              <Box className="results-card" style={styles.resultsContent}>
                <div className="resource-type">
                  {resourceTypeIconView ? resourceTypeIconView : iconView}
                  {resourceTypeLabel && <div style={styles.introText}>{resourceTypeLabel}</div>}
                  <div style={styles.introText}>{label}</div>
                </div>
                <div className="results-text">
                  {this._getResourceDisplayContent(res, resourceType)}
                </div>
              </Box>
            </div>
          );
        })
      : null;

    return <div className="results-card-container">{resourceCards}</div>;
  };

  _onTextInputChanged(fieldName, text) {
    this.setState({
      [fieldName]: text,
    });
  }

  _fetchRecordLineage = () => {
    const { currentUser } = this.props;
    const { trackerId, resquestedTrackerId } = this.state;

    if (trackerId) {
      if (!resquestedTrackerId) {
        this.setState({ resquestedTrackerId: true });
      }
      this.props.fetchRecordLineage(currentUser, trackerId);
    }
  };

  _getResourceDisplayContent = (resourceInfo = null, resourceType = null) => {
    if (!resourceInfo || !resourceType) {
      return null;
    }

    const { sources, dataSets, sinks } = this.props;

    let resourceContent = null;

    switch (resourceType) {
      case LINEAGE_RESOUCE_TYPES.data_source:
        resourceContent = reduce(
          resourceInfo,
          (acc, thisInfo, idx) => {
            let label = idx;
            let content = thisInfo;

            if (!has(PARAM_DISPLAY_NAMES.Source, idx)) {
              return acc;
            }

            label = PARAM_DISPLAY_NAMES.Source[idx];

            switch (idx) {
              case 'source_id':
              case 'id': {
                const name = has(sources, thisInfo)
                  ? `${sources[thisInfo].name} (ID: ${thisInfo})`
                  : `(ID: ${thisInfo})`;
                content = (
                  <Link to={`${this.context}${ROUTE_DATA_SOURCES}/${thisInfo}`}>{name}</Link>
                );
                break;
              }

              case 'initial_ingest_timestamp': {
                let ingestTime = !isNil(thisInfo) ? moment(thisInfo).toISOString() : null;
                content = ingestTime ? DateUtils.convertDateStringToFormat(ingestTime) : '';

                break;
              }
              default:
            }

            let thisContent = (
              <div className="result-info-row" key={idx}>
                <div className="result-info-label">{label}</div>
                <div className="result-info-content">{content}</div>
              </div>
            );
            acc.push(thisContent);
            return acc;
          },
          [],
        );
        break;

      case LINEAGE_RESOUCE_TYPES.data_set:
        resourceContent = reduce(
          resourceInfo,
          (acc, thisInfo, idx) => {
            let label = idx;
            let content = thisInfo;
            if (!has(PARAM_DISPLAY_NAMES.Dataset, idx)) {
              return acc;
            }

            label = PARAM_DISPLAY_NAMES.Dataset[idx];

            switch (idx) {
              case 'id': {
                const name = has(dataSets, thisInfo)
                  ? `${dataSets[thisInfo].name} (ID: ${thisInfo})`
                  : `(ID: ${thisInfo})`;

                content = (
                  <Link to={`${this.context}${ROUTE_MY_DATA_SETS}/${thisInfo}`}>{name}</Link>
                );
                break;
              }
              default:
            }

            let thisContent = (
              <div className="result-info-row" key={idx}>
                <div className="result-info-label">{label}</div>
                <div className="result-info-content">{content}</div>
              </div>
            );
            acc.push(thisContent);
            return acc;
          },
          [],
        );
        break;

      case LINEAGE_RESOUCE_TYPES.data_sink:
        resourceContent = reduce(
          resourceInfo,
          (acc, thisInfo, idx) => {
            let label = idx;
            let content = thisInfo;
            if (!has(PARAM_DISPLAY_NAMES.Sink, idx)) {
              return acc;
            }

            label = PARAM_DISPLAY_NAMES.Sink[idx];
            switch (idx) {
              case 'id': {
                const name = has(sinks, thisInfo)
                  ? `${sinks[thisInfo].name} (ID: ${thisInfo})`
                  : `(ID: ${thisInfo})`;
                content = (
                  <Link to={`${this.context}${ROUTE_DESTINATIONS}/${thisInfo}`}>{name}</Link>
                );
                break;
              }
              default:
            }

            let thisContent = (
              <div className="result-info-row" key={idx}>
                <div className="result-info-label">{label}</div>
                <div className="result-info-content">{content}</div>
              </div>
            );
            acc.push(thisContent);
            return acc;
          },
          [],
        );
        break;
      default:
    }

    return resourceContent;
  };
}

export default RecordLineageView;
